import { useState, useEffect, useCallback } from 'react';
import { Upload, FileText } from 'lucide-react';
import { useBulkReferralDryRun } from '../../../hooks/useBulkReferralDryRun';
import type { BulkReferralResponseDto } from '../../../types/bulk-referral.type';
import type { BulkUploadStage as StageType } from './BulkUploadSurface';

type BulkUploadStageProps = {
  onStageChange: (stage: StageType, data?: BulkReferralResponseDto) => void;
  /** Lets the surface remember the selected `File` so commit (issue 22, §7.1) can re-send it. */
  onFileSelected?: (file: File) => void;
};

export function BulkUploadStage({ onStageChange, onFileSelected }: BulkUploadStageProps) {
  const [dragActive, setDragActive] = useState(false);

  const { mutate, isPending, isError, error, data, reset } = useBulkReferralDryRun();

  useEffect(() => {
    if (data && data.fileErrors.length === 0) {
      onStageChange('reconciliation', data);
    }
  }, [data, onStageChange]);

  const handleFileSelect = useCallback(
    (file: File) => {
      onFileSelected?.(file);
      mutate({ file, dryRun: true });
    },
    [mutate, onFileSelected],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        handleFileSelect(e.dataTransfer.files[0]);
      }
    },
    [handleFileSelect],
  );

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();
      if (e.target.files && e.target.files.length > 0) {
        handleFileSelect(e.target.files[0]);
      }
    },
    [handleFileSelect],
  );

  const handleTryAgain = useCallback(() => {
    reset();
  }, [reset]);

  const fileErrors = data?.fileErrors ?? [];

  if (isError) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="w-full max-w-2xl space-y-4">
          <div className="rounded-lg border border-red-200 bg-red-50 p-6">
            <h3 className="mb-2 text-lg font-semibold text-red-900">Upload failed</h3>
            <p className="text-sm text-red-800">
              {error?.message ?? 'Something went wrong while processing the file. Please try again.'}
            </p>
          </div>
          <button
            type="button"
            onClick={handleTryAgain}
            className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (fileErrors.length > 0) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="w-full max-w-2xl space-y-4">
          <div className="rounded-lg border border-red-200 bg-red-50 p-6">
            <h3 className="mb-4 text-lg font-semibold text-red-900">File errors</h3>
            <ul className="space-y-2">
              {fileErrors.map((fileError, idx) => (
                <li key={`${fileError.message}-${idx}`} className="text-sm text-red-800">
                  {fileError.message}
                </li>
              ))}
            </ul>
          </div>
          <button
            type="button"
            onClick={handleTryAgain}
            className="w-full rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Try another file
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="grid h-full grid-cols-1 gap-6 p-6 lg:grid-cols-2">
      <div className="flex flex-col space-y-4">
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`
            relative flex min-h-[20rem] flex-col items-center justify-center rounded-lg border-2 border-dashed
            ${dragActive ? 'border-primary bg-primary/10' : 'border-gray-300 bg-gray-50'}
            transition-colors
          `}
        >
          <input
            id="file-upload"
            type="file"
            accept=".csv"
            onChange={handleChange}
            className="sr-only"
            aria-label="Browse for CSV file"
          />
          <label
            htmlFor="file-upload"
            className="flex cursor-pointer flex-col items-center justify-center"
          >
            <Upload className="mb-4 h-12 w-12 text-gray-400" aria-hidden />
            <p className="mb-2 text-base font-medium text-gray-700">
              Drag and drop your CSV here
            </p>
            <p className="text-sm text-gray-500">
              or{' '}
              <span className="font-medium text-primary hover:text-primary/80">browse</span>
            </p>
          </label>
        </div>

        {isPending && (
          <div className="space-y-2">
            <div className="h-2 w-full overflow-hidden rounded-full bg-gray-200">
              <div
                role="progressbar"
                className="h-full animate-pulse bg-primary"
                style={{ width: '100%' }}
              />
            </div>
            <p className="text-center text-sm text-gray-600">Reading and validating file...</p>
          </div>
        )}
      </div>

      <div className="flex flex-col space-y-6 rounded-lg border border-gray-200 bg-white p-6">
        <div>
          <h3 className="mb-4 text-lg font-semibold text-gray-900">Before you upload</h3>
          <ul className="space-y-3 text-sm text-gray-700">
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>
                <strong>150 rows maximum, 1 MB file size.</strong> Split larger imports into
                multiple files.
              </span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>
                <strong>Import what reconciles.</strong> Valid rows are written now. Rows with
                problems come back as a CSV you can fix and re-upload — you do not need a perfect
                file on the first pass.
              </span>
            </li>
            <li className="flex items-start">
              <span className="mr-2">•</span>
              <span>
                <strong>Email-based matching.</strong> People are identified by email only — names
                and companies are for visual confirmation.
              </span>
            </li>
          </ul>
        </div>

        <div className="border-t border-gray-200 pt-6">
          <a
            href="/manual-referral-bulk-upload-template.csv"
            download
            className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:text-primary/80"
          >
            <FileText className="h-4 w-4" aria-hidden />
            Download CSV template
          </a>
          <p className="mt-2 text-xs text-gray-500">
            Includes column headers and example rows. Instruction lines starting with # are
            optional.
          </p>
        </div>
      </div>
    </div>
  );
}
